/**
 * Meta-tag + JSON-LD schema generators for SEO landing pages.
 *
 * Helmet handles `<title>`, `<meta>` and `<link rel="canonical">` injection.
 * JSON-LD blocks are emitted as `<script type="application/ld+json">`.
 *
 * Pages use these via `<SeoHelmet>` (see templates/shared/SeoHelmet.jsx).
 */

const BASE_URL = "https://transportanything247.com";
const PROVIDER = {
  name: "Transport Anything 247",
  legalName: "Transport Anything 247",
  url: BASE_URL,
  telephone: "+447757133163",
  email: "customers@transportanything247.com",
  openingHours: "Mo-Su 00:00-23:59",
};

// ---- canonical URLs -------------------------------------------------------
export function canonicalFor(path) {
  if (!path.startsWith("/")) path = "/" + path;
  return `${BASE_URL}${path}`;
}

// ---- breadcrumb schema ---------------------------------------------------
export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${BASE_URL}${item.url}`,
    })),
  };
}

// ---- Service / LocalBusiness schema --------------------------------------
export function buildServiceSchema({ service, city = null, pageUrl }) {
  const name = city ? `${service.name} in ${city.name}` : `${service.name} across the UK`;
  const description = city ? service.metaDescLocal(city) : service.metaDescParent;
  const areaServed = city
    ? { "@type": "City", name: city.name }
    : { "@type": "Country", name: "United Kingdom" };
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    serviceType: service.name,
    description,
    provider: {
      "@type": "LocalBusiness",
      name: PROVIDER.name,
      url: PROVIDER.url,
      telephone: PROVIDER.telephone,
      email: PROVIDER.email,
      openingHours: PROVIDER.openingHours,
      areaServed: city
        ? {
          "@type": "City",
          name: city.name,
          geo: {
            "@type": "GeoCoordinates",
            latitude: city.coords.lat,
            longitude: city.coords.lng,
          },
        }
        : { "@type": "Country", name: "United Kingdom" },
    },
    areaServed,
    url: canonicalFor(pageUrl),
  };
}

// ---- LocalBusiness schema (city pages) -----------------------------------
export function buildLocalBusinessSchema({ city, pageUrl }) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: PROVIDER.name,
    url: canonicalFor(pageUrl),
    telephone: PROVIDER.telephone,
    email: PROVIDER.email,
    openingHours: PROVIDER.openingHours,
    address: {
      "@type": "PostalAddress",
      addressLocality: city.name,
      addressRegion: city.region,
      addressCountry: "GB",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: city.coords.lat,
      longitude: city.coords.lng,
    },
    areaServed: { "@type": "City", name: city.name },
  };
}

// ---- FAQ schema ----------------------------------------------------------
export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

// ---- Helper to JSON-stringify safely for HTML --------------------------
export function jsonLd(obj) {
  // Replace `</` to prevent breaking out of the script tag.
  return JSON.stringify(obj).replace(/</g, "\\u003c");
}
